import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  RefreshCw, 
  User, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Inbox, 
  X,
  HelpCircle,
  ArrowLeft,
  Calendar,
  Mail,
  Filter,
  ChevronRight,
  Tag
} from 'lucide-react';
import { API_URL } from '../../api/config';

const ViewTicketScreen = () => {
  const navigate = useNavigate();
  const { userToken, userData, signOut } = useAuth();
  
  // States
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    sortBy: 'oldest'
  });
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showSortOptions, setShowSortOptions] = useState(false);

  const fetchTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!userToken) {
        throw new Error('Authentication required. Please login again.');
      }

      const response = await fetch(`${API_URL}/support/view`, {
        headers: {
          'Authorization': `Bearer ${userToken}`
        }
      });
      
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch tickets');
      }

      // Filter tickets for current user if needed
      const userTickets = data.data.filter(ticket => ticket.email === userData.email);
      setTickets(userTickets);
      handleFiltering(userTickets, filters);
    } catch (error) {
      setError(error.message);
      
      if (error.message.includes('authentication') || error.message.includes('token')) {
        await signOut();
        navigate('/login');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleFiltering = (ticketsData, currentFilters) => {
    let filtered = [...ticketsData];

    if (currentFilters.search) {
      const searchTerm = currentFilters.search.toLowerCase();
      filtered = filtered.filter(ticket =>
        ticket.email.toLowerCase().includes(searchTerm) ||
        ticket.name.toLowerCase().includes(searchTerm) ||
        ticket.message.toLowerCase().includes(searchTerm)
      );
    }

    if (currentFilters.status !== 'all') {
      filtered = filtered.filter(ticket => ticket.status === currentFilters.status);
    }

    filtered.sort((a, b) => {
      if (currentFilters.sortBy === 'newest') {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      return new Date(a.createdAt) - new Date(b.createdAt);
    });

    setFilteredTickets(filtered);
  };

  useEffect(() => {
    if (userToken) {
      fetchTickets();
    }
  }, [userToken]);

  useEffect(() => {
    handleFiltering(tickets, filters);
  }, [filters, tickets]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchTickets();
  };



  const getStatusBgColor = (status) => {
    const colors = {
      'pending': 'bg-amber-100 text-amber-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      'resolved': 'bg-emerald-100 text-emerald-800',
      'closed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pending':
        return <Clock className="h-4 w-4 mr-1" />;
      case 'in-progress':
        return <RefreshCw className="h-4 w-4 mr-1" />;
      case 'resolved':
        return <CheckCircle className="h-4 w-4 mr-1" />;
      case 'closed':
        return <XCircle className="h-4 w-4 mr-1" />;
      default:
        return <HelpCircle className="h-4 w-4 mr-1" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  };

  const handleSortChange = (sortBy) => {
    setFilters({ ...filters, sortBy });
    setShowSortOptions(false);
  };

  // Sample tickets for display
  const sampleTickets = [
    {
      _id: "t123456",
      name: "some",
      email: "adityathakur19200@gmail.com",
      subject: "123",
      message: "123",
      status: "pending",
      createdAt: "2025-03-28T12:00:00.000Z"
    },
    {
      _id: "t123457",
      name: "eqw",
      email: "adityathakur19200@gmail.com",
      subject: "eqw",
      message: "qwe",
      status: "in-progress",
      createdAt: "2025-03-29T12:00:00.000Z"
    },
    {
      _id: "t123458",
      name: "2e21",
      email: "adityathakur19200@gmail.com",
      subject: "sc",
      message: "weffwe",
      status: "resolved",
      createdAt: "2025-03-29T15:00:00.000Z"
    },
    {
      _id: "t123459",
      name: "sdFWEF",
      email: "adityathakur19200@gmail.com",
      subject: "ASFAFD",
      message: "Wef",
      status: "closed",
      createdAt: "2025-03-29T18:00:00.000Z"
    },
    {
      _id: "t123460",
      name: "SDG",
      email: "adityathakur19200@gmail.com",
      subject: "SDFG",
      message: "SDFGSDGH",
      status: "pending",
      createdAt: "2025-03-29T20:00:00.000Z"
    }
  ];

  const displayTickets = filteredTickets.length > 0 ? filteredTickets : sampleTickets;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <button 
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 text-white"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white">Support Tickets</h1>
                <p className="text-purple-100 mt-1 opacity-90">Track and manage your support requests</p>
              </div>
            </div>
            <button 
              onClick={onRefresh}
              className="p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all text-white"
              aria-label="Refresh tickets"
            >
              <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="relative flex-grow max-w-xl">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by name, email, or message..."
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            
            <div className="relative">
              <button 
                onClick={() => setShowSortOptions(!showSortOptions)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors text-sm"
              >
                <Filter className="h-4 w-4 text-gray-500" />
                <span className="text-gray-700">Sort: {filters.sortBy === 'newest' ? 'Newest first' : 'Oldest first'}</span>
              </button>
              
              {showSortOptions && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg z-10 py-1 border border-gray-100">
                  <button 
                    className="w-full px-4 py-2 text-left hover:bg-purple-50 text-gray-700 text-sm"
                    onClick={() => handleSortChange('newest')}
                  >
                    Newest first
                  </button>
                  <button 
                    className="w-full px-4 py-2 text-left hover:bg-purple-50 text-gray-700 text-sm"
                    onClick={() => handleSortChange('oldest')}
                  >
                    Oldest first
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filter Pills */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex overflow-x-auto py-1 gap-2 scrollbar-hide">
          {['all', 'pending', 'in-progress', 'resolved', 'closed'].map((status) => (
            <button
              key={status}
              className={`px-4 py-2 rounded-full whitespace-nowrap transition-all text-sm ${
                filters.status === status
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
              onClick={() => setFilters({ ...filters, status })}
            >
              {status === 'all' ? 'All Tickets' : status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Tickets List */}
      <div className="container mx-auto px-4 py-4 flex-grow">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-12 h-12 rounded-full border-3 border-purple-200 border-t-purple-600 animate-spin mb-4"></div>
            <p className="text-gray-600 font-medium">Loading your tickets...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center text-center py-16 bg-white rounded-xl shadow-sm p-6">
            <div className="bg-red-100 p-3 rounded-full mb-4">
              <AlertCircle className="h-7 w-7 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Something went wrong</h3>
            <p className="text-red-500">{error}</p>
            <button
              onClick={onRefresh}
              className="mt-6 px-5 py-2.5 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            {displayTickets.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-16 bg-white rounded-xl shadow-sm">
                <div className="bg-gray-100 p-4 rounded-full mb-4">
                  <Inbox className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">No tickets found</h3>
                <p className="text-gray-500 max-w-md">Try adjusting your search criteria or filters to find what you're looking for.</p>
              </div>
            ) : (
              <div className="grid gap-4 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
                {displayTickets.map((ticket) => (
                  <div 
                    key={ticket._id} 
                    className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-100 hover:border-purple-200"
                    onClick={() => setSelectedTicket(ticket)}
                  >
                    <div className="p-5">
                      {/* Ticket header with user info */}
                      <div className="flex items-center mb-4">
                        <div className="bg-purple-100 rounded-full p-2 mr-3">
                          <User className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="flex-grow">
                          <h3 className="font-medium text-gray-900">{ticket.name}</h3>
                          <div className="flex items-center text-xs text-gray-500">
                            <Mail className="h-3 w-3 mr-1" />
                            <span className="truncate">{ticket.email}</span>
                          </div>
                        </div>
                        <div className={`px-2.5 py-1 rounded-full text-xs font-medium inline-flex items-center ${getStatusBgColor(ticket.status)}`}>
                          {getStatusIcon(ticket.status)}
                          <span>{ticket.status}</span>
                        </div>
                      </div>
                      
                      {/* Ticket content */}
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-800 mb-1.5">
                          {ticket.subject}
                        </h4>
                        <p className="text-xs text-gray-600 line-clamp-2 min-h-12">
                          {ticket.message}
                        </p>
                      </div>
                      
                      {/* Ticket footer */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-xs text-gray-500">
                          <Calendar className="h-3 w-3 mr-1.5" />
                          <span>Created on {formatDate(ticket.createdAt)}</span>
                        </div>
                        
                        <button className="text-purple-600 text-xs font-medium flex items-center hover:text-purple-800 transition-colors">
                          View Details
                          <ChevronRight className="h-3 w-3 ml-1" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Ticket Details Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-slide-up">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h2 className="text-lg font-bold text-gray-800">Ticket Details</h2>
              <button
                onClick={() => setSelectedTicket(null)}
                className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                aria-label="Close modal"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-grow">
              {/* Ticket header */}
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div className="flex items-center">
                  <div className="bg-purple-100 p-3 rounded-full mr-4">
                    <User className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-800">{selectedTicket.name}</h3>
                    <p className="text-sm text-gray-500">{selectedTicket.email}</p>
                  </div>
                </div>
                
                <div className={`px-4 py-1.5 rounded-lg text-sm font-medium flex items-center ${getStatusBgColor(selectedTicket.status)}`}>
                  {getStatusIcon(selectedTicket.status)}
                  <span>{selectedTicket.status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</span>
                </div>
              </div>
              
              {/* Ticket ID and dates */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6 flex flex-col md:flex-row justify-between">
                <div className="flex items-center mb-2 md:mb-0">
                  <Tag className="h-4 w-4 mr-2 text-gray-500" />
                  <span className="text-sm text-gray-600">Ticket ID: <span className="font-medium text-gray-800">#{selectedTicket._id?.substring(0, 8)}</span></span>
                </div>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                  <span className="text-sm text-gray-600">Created: <span className="font-medium text-gray-800">{formatDate(selectedTicket.createdAt)}</span></span>
                </div>
              </div>
              
              {/* Ticket subject */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Subject</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-800 font-medium">{selectedTicket.subject}</p>
                </div>
              </div>
              
              {/* Ticket message */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Message</h3>
                <div className="bg-gray-50 p-4 rounded-lg min-h-24">
                  <p className="text-gray-800 whitespace-pre-line">{selectedTicket.message}</p>
                </div>
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex gap-3">
              <button
                onClick={() => setSelectedTicket(null)}
                 className="flex-1 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-colors flex items-center justify-center"
              >
                Close
              </button>
              
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-up {
          0% { transform: translateY(20px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out forwards;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;  
          overflow: hidden;
        }
        .min-h-12 {
          min-height: 3rem;
        }
        .min-h-24 {
          min-height: 6rem;
        }
        .border-3 {
          border-width: 3px;
        }
      `}</style>
    </div>
  );
};

export default ViewTicketScreen;